import JSZip from 'jszip';
import { MAKE_A_BOX_SHELL } from './makeABoxShellString';

/**
 * Gerador de faces de caixa para impressão A4.
 * Altura (H), largura (W), comprimento (L) em mm.
 */

export function mountMakeABox(container, options) {
  var backgroundAssetSrc =
    options && options.backgroundSrc
      ? options.backgroundSrc
      : typeof process !== 'undefined' &&
          process.env &&
          process.env.PUBLIC_URL !== undefined
        ? String(process.env.PUBLIC_URL).replace(/\/$/, '') + '/box-generator/background.png'
        : '/box-generator/background.png';

  container.classList.add('bxg-root');
  container.innerHTML = MAKE_A_BOX_SHELL;

  function byId(id) {
    return container.querySelector('#mab-' + id);
  }

  var TAB_DEG = 40;
  var A4_W = 210;
  var A4_H = 297;
  /**
   * Impressão e PDF: sempre uma folha física A4 retrato 210×297 mm (nunca paisagem).
   * O desenho é sempre encaixado nessa área; peças que só caberiam em paisagem ficam bloqueadas.
   */
  var PRINT_SHEET_ORIENTATION = "portrait";
  /** Folha inteira utilizável (+ folga sub-mm contra erros float). */
  var EPS_FIT_MM = 0.05;
  /** Espaço entre peças na mesma folha quando “Juntar pequenas” está ativo. */
  var PAIR_GAP_MM = 3;
  /**
   * Em zoom 100%, altura de um ladrilho da textura no papel (mm); largura segue a proporção da imagem.
   * Zoom % multiplica este valor (maior % = ladrilhos maiores = textura mais “ampliada”).
   */
  var BG_TILE_REF_H_MM = 120;
  var TUCK_MIN = 8;
  var TUCK_MAX = 14;
  /** Preenchido a partir dos seletores do cabeçalho antes de cada render. */
  var drawTheme = {
    colCut: "#c4c8d2",
    colFold: "#b0b5c2",
    overlayRgba: "rgba(255,255,255,0)",
  };
  var STROKE_FACE = 0.35;
  var STROKE_FOLD = 0.12;

  function hexToRgb(hex) {
    var h = String(hex || "").replace("#", "").trim();
    if (h.length === 3) {
      h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    }
    if (h.length !== 6) {
      return { r: 196, g: 200, b: 210 };
    }
    var n = parseInt(h, 16);
    if (!isFinite(n)) {
      return { r: 196, g: 200, b: 210 };
    }
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }

  function rgbaFromColorOpacity(hex, opacityPct) {
    var rgb = hexToRgb(hex);
    var a = clamp(parseFloat(opacityPct), 0, 100) / 100;
    return "rgba(" + rgb.r + "," + rgb.g + "," + rgb.b + "," + a + ")";
  }

  function refreshDrawThemeFromInputs() {
    var olHexEl = byId("corOverlayFundo");
    var olOpEl = byId("opacidadeOverlayFundo");
    var lnHexEl = byId("corLinhas");
    var lnOpEl = byId("opacidadeLinhas");
    var olHex = olHexEl && olHexEl.value ? olHexEl.value : "#ffffff";
    var olOp = olOpEl && olOpEl.value !== "" ? parseFloat(olOpEl.value) : 0;
    var lnHex = lnHexEl && lnHexEl.value ? lnHexEl.value : "#c4c8d2";
    var lnOp = lnOpEl && lnOpEl.value !== "" ? parseFloat(lnOpEl.value) : 100;
    var lineRgba = rgbaFromColorOpacity(lnHex, lnOp);
    drawTheme.colCut = lineRgba;
    drawTheme.colFold = lineRgba;
    drawTheme.overlayRgba = rgbaFromColorOpacity(olHex, olOp);
  }

  function syncHeaderOpacityLabels() {
    function set(idRange, idPct) {
      var r = byId(idRange);
      var p = byId(idPct);
      if (!r || !p) return;
      var v = Math.round(clamp(parseFloat(r.value) || 0, 0, 100));
      p.textContent = v + "%";
      r.setAttribute("aria-valuenow", String(v));
    }
    set("opacidadeOverlayFundo", "opacidadeOverlayFundoPct");
    set("opacidadeLinhas", "opacidadeLinhasPct");
  }

  function rad(deg) {
    return (deg * Math.PI) / 180;
  }

  /** Recuo lateral do trapézio da aba: D / tan(40°) */
  function tabInset(depthMm) {
    return depthMm / Math.tan(rad(TAB_DEG));
  }

  function clamp(n, a, b) {
    return Math.max(a, Math.min(b, n));
  }

  function fmt(n) {
    return Math.round(n * 10) / 10;
  }

  /**
   * Transforms e atributos que definem tamanho físico no SVG — não usar fmt() (1 casa):
   * arredondar escala, ex. 0,994→1, fazia a peça ultrapassar a área útil e o navegador
   * encolher a página ao imprimir (~1 mm a menos na régua).
   */
  function svgNum(n) {
    if (typeof n !== "number" || !isFinite(n)) {
      return "0";
    }
    return String(Math.round(n * 1e6) / 1e6);
  }

  /**
   * @typedef {{ w: number, h: number, orientation: 'portrait'|'landscape', scale: number }} SheetLayout
   */

  /** Área útil = folha A4 inteira (sem margem artificial). Margens só encolhem o modelo à toa (~2 mm numa face típica). */
  function innerA4(orientation) {
    var iw = orientation === "portrait" ? A4_W : A4_H;
    var ih = orientation === "portrait" ? A4_H : A4_W;
    return { iw: iw, ih: ih };
  }

  /**
   * Melhor orientação A4 para conteúdo (w x h), sempre escala 1 — sem encolher para caber.
   * Se não couber em retrato nem paisagem, fitsWithoutShrink: false.
   * Com `forcedOrientation`, usa só essa orientação (para todas as folhas do trabalho serem iguais).
   */
  function layoutSheet(contentW, contentH, forcedOrientation) {
    var p = innerA4("portrait");
    var l = innerA4("landscape");
    var eps = EPS_FIT_MM;
    var cw = contentW;
    var ch = contentH;

    if (forcedOrientation === "portrait" || forcedOrientation === "landscape") {
      var innF = innerA4(forcedOrientation);
      var okF = cw <= innF.iw + eps && ch <= innF.ih + eps;
      var pageWF = forcedOrientation === "portrait" ? A4_W : A4_H;
      var pageHF = forcedOrientation === "portrait" ? A4_H : A4_W;
      return {
        w: pageWF,
        h: pageHF,
        orientation: forcedOrientation,
        scale: 1,
        innerW: innF.iw,
        innerH: innF.ih,
        fitsWithoutShrink: okF,
        fits: okF,
      };
    }

    var portraitOk = cw <= p.iw + eps && ch <= p.ih + eps;
    var landscapeOk = cw <= l.iw + eps && ch <= l.ih + eps;

    if (!portraitOk && !landscapeOk) {
      return {
        w: A4_W,
        h: A4_H,
        orientation: "portrait",
        scale: 1,
        innerW: p.iw,
        innerH: p.ih,
        fitsWithoutShrink: false,
        fits: false,
      };
    }

    var usePortrait;
    if (portraitOk && !landscapeOk) {
      usePortrait = true;
    } else if (!portraitOk && landscapeOk) {
      usePortrait = false;
    } else {
      var rP = Math.min(p.iw / cw, p.ih / ch);
      var rL = Math.min(l.iw / cw, l.ih / ch);
      usePortrait = rP >= rL;
    }

    var inner = usePortrait ? p : l;
    var orientation = usePortrait ? "portrait" : "landscape";
    var pageW = usePortrait ? A4_W : A4_H;
    var pageH = usePortrait ? A4_H : A4_W;
    return {
      w: pageW,
      h: pageH,
      orientation: orientation,
      scale: 1,
      innerW: inner.iw,
      innerH: inner.ih,
      fitsWithoutShrink: true,
      fits: true,
    };
  }

  /**
   * Prateleiras (linhas horizontais): preenche da esquerda para a direita; nova linha quando não cabe mais.
   * Cada linha usa altura igual à maior peça nessa linha; peças centradas na altura da linha.
   * Espaço g entre vizinhos na horizontal e também entre uma linha e a seguinte.
   *
   * @returns {{ totalW: number, totalH: number, pieceOffsets: { ox: number, oy: number }[], slack: number, innerW: number, innerH: number }|null}
   */
  function layoutShelfGreedyWithinInner(pieceList, iw, ih, gapMm) {
    var g = gapMm;
    var n = pieceList.length;
    /** @type {{ indices: number[], w: number, h: number }[]} */
    var shelves = [];
    var curIdx = [];
    var curW = 0;
    var curH = 0;

    var i;
    for (i = 0; i < n; i++) {
      var p = pieceList[i];
      var gw = curIdx.length > 0 ? g : 0;
      var nw = curW + gw + p.bboxW;
      var nh = Math.max(curH, p.bboxH);

      if (curIdx.length === 0) {
        if (p.bboxW > iw + EPS_FIT_MM) {
          return null;
        }
        curIdx.push(i);
        curW = p.bboxW;
        curH = p.bboxH;
      } else if (nw <= iw + EPS_FIT_MM) {
        curIdx.push(i);
        curW = nw;
        curH = nh;
      } else {
        shelves.push({ indices: curIdx.slice(), w: curW, h: curH });
        curIdx = [i];
        curW = p.bboxW;
        curH = p.bboxH;
        if (curW > iw + EPS_FIT_MM) {
          return null;
        }
      }
    }
    if (curIdx.length) {
      shelves.push({ indices: curIdx, w: curW, h: curH });
    }

    var totalW = 0;
    var totalH = 0;
    var si;
    for (si = 0; si < shelves.length; si++) {
      if (shelves[si].w > totalW) {
        totalW = shelves[si].w;
      }
    }
    for (si = 0; si < shelves.length; si++) {
      totalH += shelves[si].h;
      if (si < shelves.length - 1) {
        totalH += g;
      }
    }

    if (totalW > iw + EPS_FIT_MM || totalH > ih + EPS_FIT_MM) {
      return null;
    }

    var pieceOffsets = new Array(n);
    var yRun = 0;
    for (si = 0; si < shelves.length; si++) {
      var sh = shelves[si];
      var xRun = 0;
      var jj;
      for (jj = 0; jj < sh.indices.length; jj++) {
        var ii = sh.indices[jj];
        var pi = pieceList[ii];
        pieceOffsets[ii] = {
          ox: xRun,
          oy: yRun + (sh.h - pi.bboxH) / 2,
        };
        xRun += pi.bboxW;
        if (jj < sh.indices.length - 1) {
          xRun += g;
        }
      }
      yRun += sh.h;
      if (si < shelves.length - 1) {
        yRun += g;
      }
    }

    var slack = Math.min(iw - totalW, ih - totalH);
    return {
      totalW: totalW,
      totalH: totalH,
      pieceOffsets: pieceOffsets,
      slack: slack,
      innerW: iw,
      innerH: ih,
    };
  }

  /**
   * Várias peças sem redução na folha A4 (escala 1): prateleiras + retrato ou paisagem.
   * Escolhe a disposição com menor área do retângulo que envolve as peças (melhor aproveitamento).
   * @param {Array<{ bboxW: number, bboxH: number }>} pieceList
   * @returns {Record<string, unknown>|null}
   */
  function layoutGroupSheet(pieceList, gapMm, forcedOrientation) {
    var g =
      gapMm !== undefined && gapMm !== null && isFinite(Number(gapMm)) ? gapMm : PAIR_GAP_MM;
    var n = pieceList ? pieceList.length : 0;
    if (n < 2) {
      return null;
    }

    /** @type {{ arrangement: string, orientation: string, totalW: number, totalH: number, innerW: number, innerH: number, offsets: { ox: number, oy: number }[], bboxArea: number }[]} */
    var candidates = [];
    var ori;
    for (ori = 0; ori < 2; ori++) {
      var orientation = ori === 0 ? "portrait" : "landscape";
      if (forcedOrientation === "portrait" || forcedOrientation === "landscape") {
        if (orientation !== forcedOrientation) {
          continue;
        }
      }
      var inn = innerA4(orientation);
      var shelf = layoutShelfGreedyWithinInner(pieceList, inn.iw, inn.ih, g);
      if (shelf) {
        candidates.push({
          arrangement: "shelf",
          orientation: orientation,
          totalW: shelf.totalW,
          totalH: shelf.totalH,
          innerW: inn.iw,
          innerH: inn.ih,
          offsets: shelf.pieceOffsets,
          bboxArea: shelf.totalW * shelf.totalH,
          marginMin: Math.min(inn.iw - shelf.totalW, inn.ih - shelf.totalH),
        });
      }
    }

    if (!candidates.length) {
      return null;
    }

    candidates.sort(function (a, b) {
      if (Math.abs(a.bboxArea - b.bboxArea) > 1e-3) {
        return a.bboxArea - b.bboxArea;
      }
      var ap = a.orientation === "portrait" ? 1 : 0;
      var bp = b.orientation === "portrait" ? 1 : 0;
      if (bp !== ap) return bp - ap;
      return b.marginMin - a.marginMin;
    });
    var ch = candidates[0];
    var usePortrait = ch.orientation === "portrait";

    var ox = (ch.innerW - ch.totalW) / 2;
    var oy = (ch.innerH - ch.totalH) / 2;

    return {
      w: usePortrait ? A4_W : A4_H,
      h: usePortrait ? A4_H : A4_W,
      orientation: ch.orientation,
      scale: 1,
      innerW: ch.innerW,
      innerH: ch.innerH,
      fits: true,
      arrangement: ch.arrangement,
      totalW: ch.totalW,
      totalH: ch.totalH,
      pieceOffsets: ch.offsets,
      ox: ox,
      oy: oy,
    };
  }

  function bgTextureNaturalPx() {
    var w = bgTexturePxW > 0 ? bgTexturePxW : 3508;
    var h = bgTexturePxH > 0 ? bgTexturePxH : 2480;
    return { w: w, h: h };
  }

  /** @returns {{ w: number, h: number }} tamanho do ladrilho em mm (proporção da bitmap) */
  function bgTextureTileMm(zoomPct) {
    var nat = bgTextureNaturalPx();
    var z = parseFloat(zoomPct);
    if (!isFinite(z) || z <= 0) {
      z = 100;
    }
    z = clamp(z, 5, 400) / 100;
    var tileHmm = BG_TILE_REF_H_MM * z;
    var tileWmm = tileHmm * (nat.w / nat.h);
    return { w: tileWmm, h: tileHmm };
  }

  /**
   * Ladrilho do pattern: SVG interno com viewBox em pixels da textura e tamanho em mm.
   * O desenho direto da imagem em mm (ladrilho “grande” no espaço do folha) faz vários motores
   * suavizarem a bitmap a resolução baixa na tela e na impressão.
   */
  function bgPatternTileInnerSvg(tileMm) {
    var hi = tryBuildHiResPatternTileRaster(tileMm);
    if (hi && hi.dataUrl) {
      return (
        '<svg xmlns="http://www.w3.org/2000/svg" width="' +
        svgNum(tileMm.w) +
        '" height="' +
        svgNum(tileMm.h) +
        '" viewBox="0 0 ' +
        hi.pxW +
        " " +
        hi.pxH +
        '" preserveAspectRatio="none">' +
        '<image href="' +
        escapeXml(hi.dataUrl) +
        '" width="' +
        hi.pxW +
        '" height="' +
        hi.pxH +
        '" preserveAspectRatio="none"/>' +
        "</svg>"
      );
    }
    var nat = bgTextureNaturalPx();
    if (bgTexturePxW <= 0 || bgTexturePxH <= 0) {
      return (
        '<image href="' +
        escapeXml(backgroundAssetSrc) +
        '" x="0" y="0" width="' +
        svgNum(tileMm.w) +
        '" height="' +
        svgNum(tileMm.h) +
        '" preserveAspectRatio="none"/>'
      );
    }
    return (
      '<svg xmlns="http://www.w3.org/2000/svg" width="' +
      svgNum(tileMm.w) +
      '" height="' +
      svgNum(tileMm.h) +
      '" viewBox="0 0 ' +
      nat.w +
      " " +
      nat.h +
      '" preserveAspectRatio="none">' +
      '<image href="' +
      escapeXml(backgroundAssetSrc) +
      '" x="0" y="0" width="' +
      nat.w +
      '" height="' +
      nat.h +
      '" preserveAspectRatio="none"/>' +
      "</svg>"
    );
  }

  function readBgZoomPct() {
    var el = byId("zoomBackground");
    var v = parseFloat(el && el.value);
    if (!isFinite(v) || v <= 0) {
      return 100;
    }
    return clamp(v, 5, 400);
  }

  /**
   * Compensa encolhimento típico do motor de impressão (Chrome/Edge/PDF) frente aos mm do SVG.
   * 100 = sem correção; valores &gt;100 aumentam o traço no papel (experimente 101,5–103 se a régua der menos mm).
   */
  function readPrintCorrectionPct() {
    var el = byId("correcaoImpressao");
    var v = parseFloat(el && el.value);
    if (!isFinite(v) || v <= 0) {
      return 100;
    }
    return clamp(v, 95, 108);
  }

  /** Escala final no papel = escala de layout × correção %. */
  function combinedPieceScale(layoutScale) {
    return layoutScale * (readPrintCorrectionPct() / 100);
  }

  var LOCAL_STORAGE_KEY = "eagle-box-generator-config";
  var CONFIG_VERSION = 1;
  var PERSIST_DEBOUNCE_MS = 200;
  /** @type {number|undefined} */
  var persistTimer;
  /** @type {number|undefined} */
  var resizeDebounceTimer;

  function getDefaultConfig() {
    return {
      v: CONFIG_VERSION,
      altura: 40,
      largura: 60,
      comprimento: 80,
      profundidadeAba: 12,
      agruparPequenas: false,
      somenteBackground: false,
      corOverlayFundo: "#ffffff",
      opacidadeOverlayFundo: 0,
      zoomBackground: 100,
      corLinhas: "#c4c8d2",
      opacidadeLinhas: 100,
      correcaoImpressao: 100,
    };
  }

  function isValidHex6(h) {
    return /^#[0-9a-fA-F]{6}$/.test(String(h || ""));
  }

  /**
   * @param {string|null} raw
   * @returns {object}
   */
  function parseStoredConfig(raw) {
    var d = getDefaultConfig();
    if (!raw || typeof raw !== "string") {
      return d;
    }
    try {
      var o = JSON.parse(raw);
      if (!o || typeof o !== "object") {
        return d;
      }
      var keys = [
        "altura",
        "largura",
        "comprimento",
        "profundidadeAba",
        "agruparPequenas",
        "somenteBackground",
        "corOverlayFundo",
        "opacidadeOverlayFundo",
        "zoomBackground",
        "corLinhas",
        "opacidadeLinhas",
        "correcaoImpressao",
      ];
      for (var i = 0; i < keys.length; i++) {
        var k = keys[i];
        if (Object.prototype.hasOwnProperty.call(o, k)) {
          d[k] = o[k];
        }
      }
      if (typeof o.v === "number" && isFinite(o.v)) {
        d.v = o.v;
      }
      return d;
    } catch (e) {
      return d;
    }
  }

  /**
   * @returns {object}
   */
  function readConfigFromDom() {
    var def = getDefaultConfig();
    function numMm(id) {
      var el = byId(id);
      var v = parseFloat(el && el.value);
      return isFinite(v) ? v : NaN;
    }
    var h = numMm("altura");
    var w = numMm("largura");
    var l = numMm("comprimento");
    var dAb = numMm("profundidadeAba");
    var agr = byId("agruparPequenas");
    var sb = byId("somenteBackground");
    var olHexEl = byId("corOverlayFundo");
    var olOpEl = byId("opacidadeOverlayFundo");
    var lnHexEl = byId("corLinhas");
    var lnOpEl = byId("opacidadeLinhas");
    return {
      v: CONFIG_VERSION,
      altura: isFinite(h) ? h : def.altura,
      largura: isFinite(w) ? w : def.largura,
      comprimento: isFinite(l) ? l : def.comprimento,
      profundidadeAba: isFinite(dAb) ? dAb : def.profundidadeAba,
      agruparPequenas: !!(agr && agr.checked),
      somenteBackground: !!(sb && sb.checked),
      corOverlayFundo: olHexEl && olHexEl.value ? olHexEl.value : "#ffffff",
      opacidadeOverlayFundo: olOpEl && olOpEl.value !== "" ? clamp(parseFloat(olOpEl.value), 0, 100) : 0,
      zoomBackground: readBgZoomPct(),
      corLinhas: lnHexEl && lnHexEl.value ? lnHexEl.value : "#c4c8d2",
      opacidadeLinhas: lnOpEl && lnOpEl.value !== "" ? clamp(parseFloat(lnOpEl.value), 0, 100) : 100,
      correcaoImpressao: readPrintCorrectionPct(),
    };
  }

  /**
   * @param {object} cfg
   */
  function applyPersistedConfig(cfg) {
    function setNum(id, v, minV) {
      var el = byId(id);
      if (!el) return;
      var n = parseFloat(v);
      if (!isFinite(n)) return;
      el.value = String(Math.max(minV, n));
    }
    setNum("altura", cfg.altura, 1);
    setNum("largura", cfg.largura, 1);
    setNum("comprimento", cfg.comprimento, 1);
    setNum("profundidadeAba", cfg.profundidadeAba, 4);

    var def = getDefaultConfig();
    var agr = byId("agruparPequenas");
    if (agr) {
      agr.checked =
        typeof cfg.agruparPequenas === "boolean" ? cfg.agruparPequenas : def.agruparPequenas;
    }
    var sb = byId("somenteBackground");
    if (sb) {
      sb.checked =
        typeof cfg.somenteBackground === "boolean" ? cfg.somenteBackground : def.somenteBackground;
    }

    var olHex = isValidHex6(cfg.corOverlayFundo) ? cfg.corOverlayFundo : "#ffffff";
    var lnHex = isValidHex6(cfg.corLinhas) ? cfg.corLinhas : "#c4c8d2";
    var olOp = clamp(parseFloat(cfg.opacidadeOverlayFundo), 0, 100);
    var lnOp = clamp(parseFloat(cfg.opacidadeLinhas), 0, 100);
    if (!isFinite(olOp)) {
      olOp = 0;
    }
    if (!isFinite(lnOp)) {
      lnOp = 100;
    }

    var olHexEl = byId("corOverlayFundo");
    if (olHexEl) olHexEl.value = olHex;
    var olOpEl = byId("opacidadeOverlayFundo");
    if (olOpEl) olOpEl.value = String(Math.round(olOp));
    var lnHexEl = byId("corLinhas");
    if (lnHexEl) lnHexEl.value = lnHex;
    var lnOpEl = byId("opacidadeLinhas");
    if (lnOpEl) lnOpEl.value = String(Math.round(lnOp));

    var z = parseFloat(cfg.zoomBackground);
    if (!isFinite(z) || z <= 0) {
      z = 100;
    }
    z = Math.round(clamp(z, 5, 400) / 5) * 5;
    var zoomEl = byId("zoomBackground");
    if (zoomEl) {
      zoomEl.value = String(z);
    }

    var cp = parseFloat(cfg.correcaoImpressao);
    if (!isFinite(cp)) {
      cp = def.correcaoImpressao;
    }
    cp = clamp(cp, 95, 108);
    var corrEl = byId("correcaoImpressao");
    if (corrEl) {
      corrEl.value = String(cp);
    }
  }

  function persistConfig() {
    try {
      var payload = readConfigFromDom();
      payload.v = CONFIG_VERSION;
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      /* indisponível ou quota */
    }
  }

  function schedulePersistConfig() {
    clearTimeout(persistTimer);
    persistTimer = setTimeout(function () {
      persistConfig();
      persistTimer = undefined;
    }, PERSIST_DEBOUNCE_MS);
  }

  function loadPersistedConfig() {
    try {
      var raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      applyPersistedConfig(parseStoredConfig(raw));
    } catch (e) {
      /* ignore */
    }
    refreshDrawThemeFromInputs();
    syncHeaderOpacityLabels();
  }

  function escapeXml(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  /** Resolução do ladrilho rasterizado para embutir no SVG (mitiga borrão na prévia de impressão do Chrome). */
  var PRINT_TILE_RASTER_DPI = 280;
  var HI_RES_TILE_MAX_PX = 2048;

  function invalidateHiResTileCache() {
    hiResTileCache.key = "";
    hiResTileCache.dataUrl = "";
    hiResTileCache.pxW = 0;
    hiResTileCache.pxH = 0;
  }

  function tryBuildHiResPatternTileRaster(tileMm) {
    if (!bgTextureImgEl || !bgTextureImgEl.complete || bgTexturePxW <= 0 || bgTexturePxH <= 0) {
      return null;
    }
    var z = readBgZoomPct();
    var key =
      z +
      "|" +
      svgNum(tileMm.w) +
      "|" +
      svgNum(tileMm.h) +
      "|" +
      bgTexturePxW +
      "x" +
      bgTexturePxH +
      "|" +
      PRINT_TILE_RASTER_DPI;
    if (hiResTileCache.key === key && hiResTileCache.dataUrl) {
      return hiResTileCache;
    }
    var dpi = PRINT_TILE_RASTER_DPI;
    var pxW = Math.ceil((tileMm.w / 25.4) * dpi);
    var pxH = Math.ceil((tileMm.h / 25.4) * dpi);
    pxW = clamp(pxW, 48, HI_RES_TILE_MAX_PX);
    pxH = clamp(pxH, 48, HI_RES_TILE_MAX_PX);
    var cnv = document.createElement("canvas");
    cnv.width = pxW;
    cnv.height = pxH;
    var ctx = cnv.getContext("2d");
    if (!ctx) {
      return null;
    }
    if ("imageSmoothingEnabled" in ctx) {
      ctx.imageSmoothingEnabled = true;
    }
    if ("imageSmoothingQuality" in ctx) {
      ctx.imageSmoothingQuality = "high";
    }
    try {
      ctx.drawImage(bgTextureImgEl, 0, 0, bgTexturePxW, bgTexturePxH, 0, 0, pxW, pxH);
    } catch (err) {
      return null;
    }
    var dataUrl = "";
    try {
      dataUrl = cnv.toDataURL("image/png");
    } catch (err2) {
      return null;
    }
    if (!dataUrl || dataUrl.indexOf("base64,") === -1) {
      return null;
    }
    hiResTileCache.key = key;
    hiResTileCache.dataUrl = dataUrl;
    hiResTileCache.pxW = pxW;
    hiResTileCache.pxH = pxH;
    return hiResTileCache;
  }

  /**
   * Rasterização da folha inteira para PNG antes de `print()` (evita o pipeline borroso de SVG/pattern no Chrome).
   * DPI e teto de pixels: equilíbrio entre nitidez e memória do canvas.
   */
  var PRINT_PAGE_RASTER_DPI = 360;
  var PRINT_PAGE_MAX_PX = 5600;

  function getPrintPagePngPixelSize() {
    var dpi = PRINT_PAGE_RASTER_DPI;
    var outW = Math.round((A4_W / 25.4) * dpi);
    var outH = Math.round((A4_H / 25.4) * dpi);
    outW = clamp(outW, 120, PRINT_PAGE_MAX_PX);
    outH = clamp(outH, 120, PRINT_PAGE_MAX_PX);
    return { outW: outW, outH: outH };
  }

  function dataUrlToUint8Array(dataUrl) {
    var comma = dataUrl.indexOf(",");
    if (comma === -1) {
      throw new Error("invalid data url");
    }
    var b64 = dataUrl.slice(comma + 1);
    var bin = atob(b64);
    var n = bin.length;
    var arr = new Uint8Array(n);
    var i;
    for (i = 0; i < n; i++) {
      arr[i] = bin.charCodeAt(i);
    }
    return arr;
  }

  function sanitizeZipFileStem(name) {
    var s = String(name || "folha").trim();
    // eslint-disable-next-line no-control-regex -- strip Windows-invalid filename chars
    s = s.replace(/[<>:"/\\|?*\u0000-\u001f]/g, "_");
    s = s.replace(/\s+/g, " ").replace(/^\.+/, "");
    if (!s) {
      s = "folha";
    }
    if (s.length > 100) {
      s = s.substring(0, 100);
    }
    return s;
  }

  function uniqueZipStem(usedMap, base) {
    var stem = sanitizeZipFileStem(base);
    if (!usedMap[stem]) {
      usedMap[stem] = 1;
      return stem;
    }
    var k = usedMap[stem];
    usedMap[stem] = k + 1;
    return stem + "-" + k;
  }

  function absolutizeSvgResourceHrefs(clone) {
    var nodes = clone.querySelectorAll("*");
    var ni;
    for (ni = 0; ni < nodes.length; ni++) {
      var el = nodes[ni];
      var href = el.getAttribute("href");
      if (
        href &&
        !/^data:/i.test(href) &&
        !/^https?:\/\//i.test(href) &&
        !/^blob:/i.test(href)
      ) {
        try {
          el.setAttribute("href", new URL(href, window.location.href).href);
        } catch (e) {
          /* ignore */
        }
      }
      var xh = el.getAttribute("xlink:href");
      if (
        xh &&
        !/^data:/i.test(xh) &&
        !/^https?:\/\//i.test(xh) &&
        !/^blob:/i.test(xh)
      ) {
        try {
          el.setAttributeNS(
            "http://www.w3.org/1999/xlink",
            "xlink:href",
            new URL(xh, window.location.href).href
          );
        } catch (e2) {
          /* ignore */
        }
      }
    }
  }

  function svgElementToPngDataUrl(svgEl, outW, outH) {
    return new Promise(function (resolve, reject) {
      var clone = svgEl.cloneNode(true);
      if (!clone.getAttribute("xmlns")) {
        clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      }
      if (!clone.getAttribute("xmlns:xlink")) {
        clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
      }
      absolutizeSvgResourceHrefs(clone);
      var vb = svgEl.getAttribute("viewBox");
      if (vb) {
        clone.setAttribute("viewBox", vb);
      } else {
        var sw = parseFloat(String(svgEl.getAttribute("width") || "").replace(/mm$/i, ""));
        var sh = parseFloat(String(svgEl.getAttribute("height") || "").replace(/mm$/i, ""));
        if (isFinite(sw) && isFinite(sh) && sw > 0 && sh > 0) {
          clone.setAttribute("viewBox", "0 0 " + sw + " " + sh);
        } else {
          clone.setAttribute("viewBox", "0 0 " + A4_W + " " + A4_H);
        }
      }
      clone.setAttribute("width", String(outW));
      clone.setAttribute("height", String(outH));
      var str = new XMLSerializer().serializeToString(clone);
      var blob = new Blob([str], { type: "image/svg+xml;charset=utf-8" });
      var url = URL.createObjectURL(blob);
      var img = new Image();
      img.onload = function () {
        try {
          var cnv = document.createElement("canvas");
          cnv.width = outW;
          cnv.height = outH;
          var ctx = cnv.getContext("2d");
          if (!ctx) {
            URL.revokeObjectURL(url);
            reject(new Error("no canvas context"));
            return;
          }
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, outW, outH);
          ctx.drawImage(img, 0, 0, outW, outH);
          var dataUrl = cnv.toDataURL("image/png");
          URL.revokeObjectURL(url);
          resolve(dataUrl);
        } catch (err) {
          URL.revokeObjectURL(url);
          reject(err);
        }
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        reject(new Error("svg raster decode failed"));
      };
      img.src = url;
    });
  }

  function prepareSheetFramesForPrintPng() {
    var pvFrames = byId("preview");
    var frames = pvFrames ? pvFrames.querySelectorAll(".bxg-sheet__frame") : [];
    var list = Array.prototype.slice.call(frames);
    if (!list.length) {
      return Promise.reject(new Error("no sheets"));
    }
    var backups = list.map(function (f) {
      return f.innerHTML;
    });
    var px = getPrintPagePngPixelSize();
    var outW = px.outW;
    var outH = px.outH;
    return Promise.all(
      list.map(function (frame) {
        var svg = frame.querySelector("svg");
        if (!svg) {
          return Promise.resolve();
        }
        return svgElementToPngDataUrl(svg, outW, outH).then(function (dataUrl) {
          frame.innerHTML =
            '<img class="bxg-sheet-print-raster" alt="" src="' + escapeXml(dataUrl) + '" />';
        });
      })
    )
      .then(function () {
        return backups;
      })
      .catch(function (err) {
        var ii;
        for (ii = 0; ii < list.length; ii++) {
          list[ii].innerHTML = backups[ii];
        }
        throw err;
      });
  }

  function restoreSheetFramesFromBackups(backups) {
    var pvFrames = byId("preview");
    var frames = pvFrames ? pvFrames.querySelectorAll(".bxg-sheet__frame") : [];
    var list = Array.prototype.slice.call(frames);
    var ii;
    for (ii = 0; ii < list.length && ii < backups.length; ii++) {
      list[ii].innerHTML = backups[ii];
    }
  }

  function attachAfterPrintRestore(backups, onDone) {
    var done = false;
    function finish() {
      if (done) {
        return;
      }
      done = true;
      restoreSheetFramesFromBackups(backups);
      if (typeof onDone === "function") {
        onDone();
      }
    }
    window.addEventListener("afterprint", finish, { once: true });
    if (window.matchMedia) {
      var mq = window.matchMedia("print");
      function onMq() {
        if (!mq.matches) {
          if (mq.removeEventListener) {
            mq.removeEventListener("change", onMq);
          } else if (mq.removeListener) {
            mq.removeListener(onMq);
          }
          finish();
        }
      }
      if (mq.addEventListener) {
        mq.addEventListener("change", onMq);
      } else       if (mq.addListener) {
        mq.addListener(onMq);
      }
    }
  }

  function triggerDownloadBlob(blob, filename) {
    var a = document.createElement("a");
    var u = URL.createObjectURL(blob);
    a.href = u;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(function () {
      URL.revokeObjectURL(u);
    }, 2500);
  }

  function buildZipFromPreviewPngBlob() {
    var pvSheets = byId("preview");
    var sheets = pvSheets ? pvSheets.querySelectorAll(".bxg-sheet") : [];
    var list = Array.prototype.slice.call(sheets);
    if (!list.length) {
      return Promise.reject(new Error("no sheets"));
    }
    var px = getPrintPagePngPixelSize();
    var zip = new JSZip();
    var used = {};
    return list.reduce(function (seq, sheet) {
      return seq.then(function () {
        var frame = sheet.querySelector(".bxg-sheet__frame");
        if (!frame) {
          return;
        }
        var svg = frame.querySelector("svg");
        if (!svg) {
          return;
        }
        var strong = sheet.querySelector(".bxg-sheet__meta strong");
        var rawBase =
          sheet.getAttribute("data-zip-base") ||
          (strong && strong.textContent ? strong.textContent : "") ||
          "folha";
        var stem = uniqueZipStem(used, rawBase);
        return svgElementToPngDataUrl(svg, px.outW, px.outH).then(function (dataUrl) {
          zip.file(stem + ".png", dataUrlToUint8Array(dataUrl));
        });
      });
    }, Promise.resolve()).then(function () {
      return zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 },
      });
    });
  }

  /**
   * Contorno da aba (sem o segmento de dobra junto à face).
   */
  function tabRedPathD(edge, x, y, rw, rh, depth) {
    var inset = tabInset(depth);
    if (edge === "top") {
      return (
        "M " +
        x +
        " " +
        y +
        " L " +
        (x + inset) +
        " " +
        (y - depth) +
        " L " +
        (x + rw - inset) +
        " " +
        (y - depth) +
        " L " +
        (x + rw) +
        " " +
        y
      );
    }
    if (edge === "bottom") {
      return (
        "M " +
        x +
        " " +
        (y + rh) +
        " L " +
        (x + inset) +
        " " +
        (y + rh + depth) +
        " L " +
        (x + rw - inset) +
        " " +
        (y + rh + depth) +
        " L " +
        (x + rw) +
        " " +
        (y + rh)
      );
    }
    if (edge === "left") {
      return (
        "M " +
        x +
        " " +
        y +
        " L " +
        (x - depth) +
        " " +
        (y + inset) +
        " L " +
        (x - depth) +
        " " +
        (y + rh - inset) +
        " L " +
        x +
        " " +
        (y + rh)
      );
    }
    return (
      "M " +
      (x + rw) +
      " " +
      y +
      " L " +
      (x + rw + depth) +
      " " +
      (y + inset) +
      " L " +
      (x + rw + depth) +
      " " +
      (y + rh - inset) +
      " L " +
      (x + rw) +
      " " +
      (y + rh)
    );
  }

  function foldLineSvg(fl) {
    return (
      '<line x1="' +
      fl.x1 +
      '" y1="' +
      fl.y1 +
      '" x2="' +
      fl.x2 +
      '" y2="' +
      fl.y2 +
      '" stroke="' +
      drawTheme.colFold +
      '" stroke-width="' +
      STROKE_FOLD +
      '"/>'
    );
  }

  function foldLineForTab(edge, x, y, rw, rh) {
    if (edge === "top") return { x1: x, y1: y, x2: x + rw, y2: y };
    if (edge === "bottom") return { x1: x, y1: y + rh, x2: x + rw, y2: y + rh };
    if (edge === "left") return { x1: x, y1: y, x2: x, y2: y + rh };
    return { x1: x + rw, y1: y, x2: x + rw, y2: y + rh };
  }

  /**
   * Contorno exterior fechado (face + abas trapézio) para clipar o fundo ao molde de recorte.
   */
  function clipHullFourTabs(x, y, rw, rh, D) {
    var inset = tabInset(D);
    return (
      "M " +
      fmt(x + inset) +
      " " +
      fmt(y - D) +
      " L " +
      fmt(x + rw - inset) +
      " " +
      fmt(y - D) +
      " L " +
      fmt(x + rw) +
      " " +
      fmt(y) +
      " L " +
      fmt(x + rw + D) +
      " " +
      fmt(y + inset) +
      " L " +
      fmt(x + rw + D) +
      " " +
      fmt(y + rh - inset) +
      " L " +
      fmt(x + rw) +
      " " +
      fmt(y + rh) +
      " L " +
      fmt(x + rw - inset) +
      " " +
      fmt(y + rh + D) +
      " L " +
      fmt(x + inset) +
      " " +
      fmt(y + rh + D) +
      " L " +
      fmt(x) +
      " " +
      fmt(y + rh) +
      " L " +
      fmt(x - D) +
      " " +
      fmt(y + rh - inset) +
      " L " +
      fmt(x - D) +
      " " +
      fmt(y + inset) +
      " L " +
      fmt(x) +
      " " +
      fmt(y) +
      " Z"
    );
  }

  /** Contorno só da face (sem abas), para fundo colado pelas laterais. */
  function clipHullPlainRect(x, y, rw, rh) {
    return (
      "M " +
      fmt(x) +
      " " +
      fmt(y) +
      " L " +
      fmt(x + rw) +
      " " +
      fmt(y) +
      " L " +
      fmt(x + rw) +
      " " +
      fmt(y + rh) +
      " L " +
      fmt(x) +
      " " +
      fmt(y + rh) +
      " Z"
    );
  }

  /** Face retangular + aba trapézio só em baixo (colagem no fundo). */
  function clipHullBottomTab(x, y, rw, rh, D) {
    var inset = tabInset(D);
    return (
      "M " +
      fmt(x) +
      " " +
      fmt(y) +
      " L " +
      fmt(x + rw) +
      " " +
      fmt(y) +
      " L " +
      fmt(x + rw) +
      " " +
      fmt(y + rh) +
      " L " +
      fmt(x + rw - inset) +
      " " +
      fmt(y + rh + D) +
      " L " +
      fmt(x + inset) +
      " " +
      fmt(y + rh + D) +
      " L " +
      fmt(x) +
      " " +
      fmt(y + rh) +
      " Z"
    );
  }

  /** Tampa: topo com aba trapézio; frente com aba de encaixe (delta, tuck). */
  function clipHullTampa(x, y, L, W, D, tuck, delta) {
    var inset = tabInset(D);
    return (
      "M " +
      fmt(x + inset) +
      " " +
      fmt(y - D) +
      " L " +
      fmt(x + L - inset) +
      " " +
      fmt(y - D) +
      " L " +
      fmt(x + L) +
      " " +
      fmt(y) +
      " L " +
      fmt(x + L) +
      " " +
      fmt(y + W) +
      " L " +
      fmt(x + L - delta) +
      " " +
      fmt(y + W + tuck) +
      " L " +
      fmt(x + delta) +
      " " +
      fmt(y + W + tuck) +
      " L " +
      fmt(x) +
      " " +
      fmt(y + W) +
      " L " +
      fmt(x) +
      " " +
      fmt(y) +
      " Z"
    );
  }

  var clipIdSeq = 0;
  function nextClipId() {
    clipIdSeq += 1;
    return "box-clip-" + clipIdSeq;
  }

  var bgPatternSeq = 0;
  function nextBgPatternId() {
    bgPatternSeq += 1;
    return "bg-tile-" + bgPatternSeq;
  }

  /** Dimensões em px de `background.png` (preenchido após preload; fallback no SVG). */
  var bgTexturePxW = 0;
  var bgTexturePxH = 0;
  /** Referência ao bitmap carregado (para rasterizar o ladrilho em alta na composição do SVG). */
  var bgTextureImgEl = null;
  /** Cache do ladrilho embutido (data URL); invalidado ao mudar zoom ou recarregar a textura. */
  var hiResTileCache = { key: "", dataUrl: "", pxW: 0, pxH: 0 };

  /**
   * @typedef {{ label: string, svgInner: string, bboxW: number, bboxH: number, clipHullD: string, warnings: string[] }} Piece
   */

  function pieceRectFourTabs(rw, rh, D, label) {
    var pad = D;
    var x = pad;
    var y = pad;
    var cuts = [];
    var folds = [];
    ["top", "bottom", "left", "right"].forEach(function (e) {
      cuts.push(
        '<path fill="none" stroke="' +
          drawTheme.colCut +
          '" stroke-width="' +
          STROKE_FACE +
          '" d="' +
          tabRedPathD(e, x, y, rw, rh, D) +
          '"/>'
      );
      folds.push(foldLineSvg(foldLineForTab(e, x, y, rw, rh)));
    });
    cuts.push(
      '<rect x="' +
        x +
        '" y="' +
        y +
        '" width="' +
        rw +
        '" height="' +
        rh +
        '" fill="none" stroke="' +
        drawTheme.colCut +
        '" stroke-width="' +
        STROKE_FACE +
        '"/>'
    );
    return {
      label: label,
      svgInner: cuts.concat(folds).join("\n"),
      bboxW: pad + rw + pad,
      bboxH: pad + rh + pad,
      clipHullD: clipHullFourTabs(x, y, rw, rh, D),
      warnings: [],
    };
  }

  /** Fundo: só retângulo; laterais/frente/costas colam nele (sem abas no fundo). */
  function pieceRectPlain(rw, rh, D, label) {
    var pad = D;
    var x = pad;
    var y = pad;
    var cuts = [
      '<rect x="' +
        x +
        '" y="' +
        y +
        '" width="' +
        rw +
        '" height="' +
        rh +
        '" fill="none" stroke="' +
        drawTheme.colCut +
        '" stroke-width="' +
        STROKE_FACE +
        '"/>',
    ];
    return {
      label: label,
      svgInner: cuts.join("\n"),
      bboxW: pad + rw + pad,
      bboxH: pad + rh + pad,
      clipHullD: clipHullPlainRect(x, y, rw, rh),
      warnings: [],
    };
  }

  /** Frente/costas: aba trapézio só na base (colagem no fundo); sem abas laterais nem superior. */
  function pieceRectBottomTabOnly(rw, rh, D, label) {
    var pad = D;
    var x = pad;
    var y = pad;
    var cuts = [];
    var folds = [];
    cuts.push(
      '<path fill="none" stroke="' +
        drawTheme.colCut +
        '" stroke-width="' +
        STROKE_FACE +
        '" d="' +
        tabRedPathD("bottom", x, y, rw, rh, D) +
        '"/>'
    );
    folds.push(foldLineSvg(foldLineForTab("bottom", x, y, rw, rh)));
    cuts.push(
      '<rect x="' +
        x +
        '" y="' +
        y +
        '" width="' +
        rw +
        '" height="' +
        rh +
        '" fill="none" stroke="' +
        drawTheme.colCut +
        '" stroke-width="' +
        STROKE_FACE +
        '"/>'
    );
    return {
      label: label,
      svgInner: cuts.concat(folds).join("\n"),
      bboxW: pad + rw + pad,
      bboxH: pad + rh + D + pad,
      clipHullD: clipHullBottomTab(x, y, rw, rh, D),
      warnings: [],
    };
  }

  function buildFundo(L, W, D) {
    return pieceRectPlain(L, W, D, "Fundo");
  }

  function buildFrente(L, H, D) {
    return pieceRectBottomTabOnly(L, H, D, "Frente");
  }

  function buildCostas(L, H, D) {
    return pieceRectBottomTabOnly(L, H, D, "Costas");
  }

  function buildLateral(W, H, D, label) {
    return pieceRectFourTabs(W, H, D, label);
  }

  /**
   * Tampa: aba trapézio de colagem só nas costas (topo); laterais retas, sem abas.
   * Aba de encaixe na frente (base) em trapézio com ângulo ~60° entre a base e os lados.
   */
  function buildTampa(L, W, D) {
    var tuck = clamp(Math.min(L, W) * 0.12, TUCK_MIN, TUCK_MAX);
    var TUCK_BASE_DEG = 60;
    var pad = D;
    var x = pad;
    var y = pad;
    var cuts = [];
    var folds = [];

    cuts.push(
      '<path fill="none" stroke="' +
        drawTheme.colCut +
        '" stroke-width="' +
        STROKE_FACE +
        '" d="' +
        tabRedPathD("top", x, y, L, W, D) +
        '"/>'
    );
    folds.push(foldLineSvg(foldLineForTab("top", x, y, L, W)));

    cuts.push(
      '<rect x="' +
        x +
        '" y="' +
        y +
        '" width="' +
        L +
        '" height="' +
        W +
        '" fill="none" stroke="' +
        drawTheme.colCut +
        '" stroke-width="' +
        STROKE_FACE +
        '"/>'
    );

    var delta = tuck / Math.tan(rad(TUCK_BASE_DEG));
    var maxDelta = Math.max(0, (L - 4) / 2);
    if (delta > maxDelta) {
      delta = maxDelta;
    }

    cuts.push(
      '<path fill="none" stroke="' +
        drawTheme.colCut +
        '" stroke-width="' +
        STROKE_FACE +
        '" d="M ' +
        x +
        " " +
        (y + W) +
        " L " +
        (x + delta) +
        " " +
        (y + W + tuck) +
        " L " +
        (x + L - delta) +
        " " +
        (y + W + tuck) +
        " L " +
        (x + L) +
        " " +
        (y + W) +
        '"/>'
    );
    folds.push(foldLineSvg(foldLineForTab("bottom", x, y, L, W)));

    var bboxW = pad + L + pad;
    var bboxH = pad + W + tuck + pad;
    return {
      label: "Tampa",
      svgInner: cuts.concat(folds).join("\n"),
      bboxW: bboxW,
      bboxH: bboxH,
      clipHullD: clipHullTampa(x, y, L, W, D, tuck, delta),
      warnings: [],
    };
  }

  function wrapPieceInA4(piece, H, W, L, D, bgZoomPct, sheetOrientation) {
    var layout = layoutSheet(piece.bboxW, piece.bboxH, sheetOrientation);
    if (!layout.fitsWithoutShrink) {
      return {
        ok: false,
        blockingErrors: [
          "Peça “" +
            piece.label +
            "” não cabe na folha A4 retrato (210×297 mm) à escala 100&nbsp;%. Reduza altura, largura, comprimento ou a profundidade da aba.",
        ],
      };
    }
    var corrPct = readPrintCorrectionPct();
    var ps = combinedPieceScale(layout.scale);
    if (
      piece.bboxW * ps > layout.innerW + EPS_FIT_MM ||
      piece.bboxH * ps > layout.innerH + EPS_FIT_MM
    ) {
      return {
        ok: false,
        blockingErrors: [
          "Peça “" +
            piece.label +
            "” com correção de impressão a " +
            corrPct +
            "% ultrapassa a área útil do A4 retrato (210×297 mm). Baixe “Correção impressão (%)” ou reduza as dimensões da caixa.",
        ],
      };
    }
    var tile = bgTextureTileMm(bgZoomPct);
    var bgPatId = nextBgPatternId();
    var warnings = piece.warnings.slice();
    var ox = (layout.innerW - piece.bboxW * ps) / 2;
    var oy = (layout.innerH - piece.bboxH * ps) / 2;
    var clipId = nextClipId();
    var svg =
      '<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" width="' +
      layout.w +
      'mm" height="' +
      layout.h +
      'mm" viewBox="0 0 ' +
      layout.w +
      " " +
      layout.h +
      '">' +
      '<rect x="0" y="0" width="' +
      layout.w +
      '" height="' +
      layout.h +
      '" fill="#ffffff"/>' +
      '<g transform="translate(' +
      svgNum(ox) +
      " " +
      svgNum(oy) +
      ") scale(" +
      svgNum(ps) +
      ')">' +
      "<defs>" +
      '<clipPath id="' +
      escapeXml(clipId) +
      '"><path d="' +
      escapeXml(piece.clipHullD) +
      '"/></clipPath>' +
      '<pattern id="' +
      escapeXml(bgPatId) +
      '" patternUnits="userSpaceOnUse" patternContentUnits="userSpaceOnUse" width="' +
      svgNum(tile.w) +
      '" height="' +
      svgNum(tile.h) +
      '">' +
      bgPatternTileInnerSvg(tile) +
      "</pattern>" +
      "</defs>" +
      '<rect x="0" y="0" width="' +
      svgNum(piece.bboxW) +
      '" height="' +
      svgNum(piece.bboxH) +
      '" fill="url(#' +
      escapeXml(bgPatId) +
      ')" clip-path="url(#' +
      escapeXml(clipId) +
      ')"/>' +
      (function () {
        var ol = drawTheme.overlayRgba;
        if (!ol || ol === "rgba(255,255,255,0)" || /,\s*0\)$/.test(ol)) {
          return "";
        }
        return (
          '<rect x="0" y="0" width="' +
          svgNum(piece.bboxW) +
          '" height="' +
          svgNum(piece.bboxH) +
          '" fill="' +
          escapeXml(ol) +
          '" clip-path="url(#' +
          escapeXml(clipId) +
          ')"/>'
        );
      })() +
      piece.svgInner +
      "</g>" +
      "</svg>";
    return {
      ok: true,
      svg: svg,
      layout: layout,
      warnings: warnings,
      piece: piece,
      effectiveScale: ps,
      printCorrectionPct: corrPct,
    };
  }

  /**
   * Várias peças na mesma folha (≥2). Use `opts.multiLayout` / `opts.pairLayout` se já calculou o layout.
   */
  function wrapPiecesInA4(parts, bgZoomPct, opts) {
    opts = opts || {};
    var backgroundOnly = !!opts.backgroundOnly;
    var n = parts.length;
    if (n < 2) {
      return null;
    }
    var lay =
      opts.multiLayout !== undefined && opts.multiLayout !== null
        ? opts.multiLayout
        : opts.pairLayout !== undefined && opts.pairLayout !== null
          ? opts.pairLayout
          : layoutGroupSheet(
              parts,
              undefined,
              opts.forcedSheetOrientation !== undefined && opts.forcedSheetOrientation !== null
                ? opts.forcedSheetOrientation
                : PRINT_SHEET_ORIENTATION
            );
    if (!lay || !lay.pieceOffsets || lay.pieceOffsets.length !== n) {
      return null;
    }

    var corrPct = readPrintCorrectionPct();
    var ps = combinedPieceScale(lay.scale);
    if (
      lay.totalW * ps > lay.innerW + EPS_FIT_MM ||
      lay.totalH * ps > lay.innerH + EPS_FIT_MM
    ) {
      var labelJoinErr = parts
        .map(function (p) {
          return p.label;
        })
        .join(" + ");
      return {
        ok: false,
        blockingErrors: [
          "Folha com “" +
            labelJoinErr +
            "”: com correção de impressão a " +
            corrPct +
            "% o grupo ultrapassa o A4 retrato (210×297 mm). Baixe “Correção impressão (%)”, reduza dimensões ou desative “Juntar pequenas”.",
        ],
      };
    }
    var gOx = (lay.innerW - lay.totalW * ps) / 2;
    var gOy = (lay.innerH - lay.totalH * ps) / 2;

    var tile = bgTextureTileMm(bgZoomPct);

    function overlayRectsForPiece(piece, clipId) {
      var ol = drawTheme.overlayRgba;
      if (!ol || ol === "rgba(255,255,255,0)" || /,\s*0\)$/.test(ol)) {
        return "";
      }
      return (
        '<rect x="0" y="0" width="' +
        svgNum(piece.bboxW) +
        '" height="' +
        svgNum(piece.bboxH) +
        '" fill="' +
        escapeXml(ol) +
        '" clip-path="url(#' +
        escapeXml(clipId) +
        ')"/>'
      );
    }

    function patternDefShared(patId) {
      return (
        '<pattern id="' +
        escapeXml(patId) +
        '" patternUnits="userSpaceOnUse" patternContentUnits="userSpaceOnUse" width="' +
        svgNum(tile.w) +
        '" height="' +
        svgNum(tile.h) +
        '">' +
        bgPatternTileInnerSvg(tile) +
        "</pattern>"
      );
    }

    var sharedBgPatId = nextBgPatternId();
    var defsPieces = patternDefShared(sharedBgPatId);
    var bodyPieces = "";
    var wi;
    for (wi = 0; wi < n; wi++) {
      var pi = parts[wi];
      var ofs = lay.pieceOffsets[wi];
      var clipId = nextClipId();
      defsPieces +=
        '<clipPath id="' +
        escapeXml(clipId) +
        '"><path d="' +
        escapeXml(pi.clipHullD) +
        '"/></clipPath>';
      bodyPieces +=
        '<g transform="translate(' +
        svgNum(ofs.ox) +
        " " +
        svgNum(ofs.oy) +
        ')">' +
        '<rect x="0" y="0" width="' +
        svgNum(pi.bboxW) +
        '" height="' +
        svgNum(pi.bboxH) +
        '" fill="url(#' +
        escapeXml(sharedBgPatId) +
        ')" clip-path="url(#' +
        escapeXml(clipId) +
        ')"/>' +
        overlayRectsForPiece(pi, clipId) +
        (backgroundOnly ? "" : pi.svgInner) +
        "</g>";
    }

    var innerG =
      '<g transform="translate(' +
      svgNum(gOx) +
      " " +
      svgNum(gOy) +
      ") scale(" +
      svgNum(ps) +
      ')">' +
      "<defs>" +
      defsPieces +
      "</defs>" +
      bodyPieces +
      "</g>";

    var svg =
      '<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" width="' +
      lay.w +
      'mm" height="' +
      lay.h +
      'mm" viewBox="0 0 ' +
      lay.w +
      " " +
      lay.h +
      '">' +
      '<rect x="0" y="0" width="' +
      lay.w +
      '" height="' +
      lay.h +
      '" fill="#ffffff"/>' +
      innerG +
      "</svg>";

    var warnings = [];
    for (wi = 0; wi < n; wi++) {
      var wj;
      for (wj = 0; wj < parts[wi].warnings.length; wj++) {
        warnings.push(parts[wi].warnings[wj]);
      }
    }
    var labelJoin = parts
      .map(function (p) {
        return p.label;
      })
      .join(" + ");
    return {
      ok: true,
      svg: svg,
      layout: lay,
      warnings: warnings,
      labelJoined: labelJoin,
      pieces: parts,
      effectiveScale: ps,
      printCorrectionPct: corrPct,
    };
  }

  /**
   * Folha A4 retrato: textura em mosaico (mesmo ladrilho mm que nas faces, via zoom %).
   */
  function buildFullPageBackgroundSvg(bgZoomPct) {
    var w = A4_W;
    var h = A4_H;
    var tile = bgTextureTileMm(bgZoomPct);
    var patId = nextBgPatternId();
    var overlayRect = "";
    var ol = drawTheme.overlayRgba;
    if (ol && ol !== "rgba(255,255,255,0)" && !/,\s*0\)$/.test(ol)) {
      overlayRect =
        '<rect x="0" y="0" width="' +
        svgNum(w) +
        '" height="' +
        svgNum(h) +
        '" fill="' +
        escapeXml(ol) +
        '"/>';
    }
    return (
      '<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" width="' +
      w +
      'mm" height="' +
      h +
      'mm" viewBox="0 0 ' +
      w +
      " " +
      h +
      '">' +
      "<defs>" +
      '<pattern id="' +
      escapeXml(patId) +
      '" patternUnits="userSpaceOnUse" patternContentUnits="userSpaceOnUse" width="' +
      svgNum(tile.w) +
      '" height="' +
      svgNum(tile.h) +
      '">' +
      bgPatternTileInnerSvg(tile) +
      "</pattern>" +
      "</defs>" +
      '<rect x="0" y="0" width="' +
      w +
      '" height="' +
      h +
      '" fill="#ffffff"/>' +
      '<rect x="0" y="0" width="' +
      w +
      '" height="' +
      h +
      '" fill="url(#' +
      escapeXml(patId) +
      ')"/>' +
      overlayRect +
      "</svg>"
    );
  }

  function buildAllPieces(H, W, L, D) {
    return [
      buildTampa(L, W, D),
      buildFundo(L, W, D),
      buildLateral(W, H, D, "Lateral Esquerda"),
      buildLateral(W, H, D, "Lateral Direita"),
      buildFrente(L, H, D),
      buildCostas(L, H, D),
    ];
  }

  function readInputs() {
    function num(id) {
      var el = byId(id);
      var v = parseFloat(el && el.value);
      return isFinite(v) ? v : NaN;
    }
    return {
      H: num("altura"),
      W: num("largura"),
      L: num("comprimento"),
      D: num("profundidadeAba"),
    };
  }

  function render() {
    refreshDrawThemeFromInputs();
    syncHeaderOpacityLabels();
    var ins = readInputs();
    var alertEl = byId("alertas");
    var preview = byId("preview");
    var btnImprimir = byId("btnImprimir");
    var btnBaixarZip = byId("btnBaixarZip");
    alertEl.innerHTML = "";
    preview.innerHTML = "";

    function setPreviewBlocked(blocked) {
      container.classList.toggle("print-blocked", !!blocked);
      if (btnImprimir) {
        btnImprimir.disabled = !!blocked;
        btnImprimir.setAttribute("aria-disabled", blocked ? "true" : "false");
      }
      if (btnBaixarZip && btnBaixarZip.getAttribute("data-zip-busy") !== "1") {
        btnBaixarZip.disabled = !!blocked;
        btnBaixarZip.setAttribute("aria-disabled", blocked ? "true" : "false");
      }
    }

    if (!isFinite(ins.H) || !isFinite(ins.W) || !isFinite(ins.L) || !isFinite(ins.D)) {
      alertEl.innerHTML =
        '<div class="bxg-alert bxg-alert--error">Preencha todos os valores numéricos.</div>';
      setPreviewBlocked(true);
      return;
    }
    if (ins.H <= 0 || ins.W <= 0 || ins.L <= 0 || ins.D <= 0) {
      alertEl.innerHTML =
        '<div class="bxg-alert bxg-alert--error">Dimensões e profundidade da aba devem ser positivas.</div>';
      setPreviewBlocked(true);
      return;
    }

    var maxPreviewW = Math.min(920, window.innerWidth - 40);
    var sbEl = byId("somenteBackground");
    var somenteBackground = !!(sbEl && sbEl.checked);
    var bgZoomPct = readBgZoomPct();

    if (somenteBackground) {
      var sheetBg = document.createElement("div");
      sheetBg.className = "bxg-sheet bxg-sheet--portrait";
      sheetBg.setAttribute("data-zip-base", "Só background");
      var metaBg = document.createElement("div");
      metaBg.className = "bxg-sheet__meta";
      metaBg.innerHTML =
        "<strong>Só background</strong> · A4 retrato · zoom " +
        Math.round(bgZoomPct) +
        "%";
      var frameBg = document.createElement("div");
      frameBg.className = "bxg-sheet__frame";
      frameBg.style.width = maxPreviewW + "px";
      frameBg.style.height = "auto";
      frameBg.innerHTML = buildFullPageBackgroundSvg(bgZoomPct);
      sheetBg.appendChild(metaBg);
      sheetBg.appendChild(frameBg);
      preview.appendChild(sheetBg);
      setPreviewBlocked(false);
      return;
    }

    var pieces = buildAllPieces(ins.H, ins.W, ins.L, ins.D);
    var agrupEl = byId("agruparPequenas");
    var agruparPequenas = !!(agrupEl && agrupEl.checked);
    var globalOri = PRINT_SHEET_ORIENTATION;
    var blockingErrors = [];
    var plans = [];

    for (var i = 0; i < pieces.length; ) {
      if (agruparPequenas) {
        var chunkGroup = [pieces[i]];
        var jg = i + 1;
        while (jg < pieces.length) {
          var trialGroup = chunkGroup.concat([pieces[jg]]);
          if (layoutGroupSheet(trialGroup, undefined, globalOri)) {
            chunkGroup = trialGroup;
            jg++;
          } else {
            break;
          }
        }
        if (chunkGroup.length >= 2) {
          var multiLay = layoutGroupSheet(chunkGroup, undefined, globalOri);
          var wrappedGroup = multiLay
            ? wrapPiecesInA4(chunkGroup, bgZoomPct, {
                multiLayout: multiLay,
                forcedSheetOrientation: globalOri,
              })
            : null;
          if (wrappedGroup) {
            if (wrappedGroup.ok === false) {
              var bem = wrappedGroup.blockingErrors || [];
              for (var em = 0; em < bem.length; em++) blockingErrors.push(bem[em]);
            } else {
              plans.push({ kind: "multi", wrapped: wrappedGroup });
            }
            i += chunkGroup.length;
            continue;
          }
        }
      }

      var wrappedOne = wrapPieceInA4(
        pieces[i],
        ins.H,
        ins.W,
        ins.L,
        ins.D,
        bgZoomPct,
        globalOri
      );
      if (wrappedOne.ok === false) {
        var be1 = wrappedOne.blockingErrors || [];
        for (var e1 = 0; e1 < be1.length; e1++) blockingErrors.push(be1[e1]);
      } else {
        plans.push({ kind: "single", wrapped: wrappedOne });
      }
      i += 1;
    }

    if (blockingErrors.length) {
      var uniqErr = [];
      for (var ue = 0; ue < blockingErrors.length; ue++) {
        if (uniqErr.indexOf(blockingErrors[ue]) === -1) uniqErr.push(blockingErrors[ue]);
      }
      alertEl.innerHTML =
        '<div class="bxg-alert bxg-alert--error">' +
        uniqErr.map(function (t) { return "<div>" + escapeXml(t) + "</div>"; }).join("") +
        "</div>";
      setPreviewBlocked(true);
      return;
    }

    var allWarnings = [];
    var pi;
    for (pi = 0; pi < plans.length; pi++) {
      var wpl = plans[pi].wrapped.warnings;
      for (var ww = 0; ww < wpl.length; ww++) allWarnings.push(wpl[ww]);
    }

    for (pi = 0; pi < plans.length; pi++) {
      if (plans[pi].kind === "multi") {
        var wrappedM = plans[pi].wrapped;
        var sheetM = document.createElement("div");
        sheetM.className = "bxg-sheet bxg-sheet--portrait";
        sheetM.setAttribute(
          "data-zip-base",
          wrappedM.labelJoined.split(" + ").join(" e ")
        );
        var metaM = document.createElement("div");
        metaM.className = "bxg-sheet__meta";
        metaM.innerHTML =
          "<strong>" +
          escapeXml(wrappedM.labelJoined) +
          "</strong> · A4 210×297 mm · " +
          Math.round(wrappedM.effectiveScale * 100) +
          "% · corr. imp. " +
          wrappedM.printCorrectionPct +
          "%";

        var frameM = document.createElement("div");
        frameM.className = "bxg-sheet__frame";
        frameM.style.width = maxPreviewW + "px";
        frameM.style.height = "auto";
        frameM.innerHTML = wrappedM.svg;
        sheetM.appendChild(metaM);
        sheetM.appendChild(frameM);
        preview.appendChild(sheetM);
      } else {
        var wrapped = plans[pi].wrapped;
        var sheet = document.createElement("div");
        sheet.className = "bxg-sheet bxg-sheet--portrait";
        sheet.setAttribute("data-zip-base", wrapped.piece.label);
        var meta = document.createElement("div");
        meta.className = "bxg-sheet__meta";
        meta.innerHTML =
          "<strong>" +
          escapeXml(wrapped.piece.label) +
          "</strong> · A4 210×297 mm · " +
          Math.round(wrapped.effectiveScale * 100) +
          "% · corr. imp. " +
          wrapped.printCorrectionPct +
          "%";

        var frame = document.createElement("div");
        frame.className = "bxg-sheet__frame";
        frame.style.width = maxPreviewW + "px";
        frame.style.height = "auto";
        frame.innerHTML = wrapped.svg;
        sheet.appendChild(meta);
        sheet.appendChild(frame);
        preview.appendChild(sheet);
      }
    }

    if (allWarnings.length) {
      var uniq = [];
      for (var u = 0; u < allWarnings.length; u++) {
        if (uniq.indexOf(allWarnings[u]) === -1) uniq.push(allWarnings[u]);
      }
      alertEl.innerHTML =
        '<div class="bxg-alert">' + uniq.map(function (t) { return "<div>" + escapeXml(t) + "</div>"; }).join("") + "</div>";
    }
    setPreviewBlocked(false);
  }

  byId("btnGerar").addEventListener("click", function () {
    persistConfig();
    render();
  });
  byId("somenteBackground").addEventListener("change", function () {
    schedulePersistConfig();
    render();
  });
  var agrupPeq = byId("agruparPequenas");
  if (agrupPeq) {
    agrupPeq.addEventListener("change", function () {
      schedulePersistConfig();
      render();
    });
  }
  ["altura", "largura", "comprimento", "profundidadeAba"].forEach(function (id) {
    var el = byId(id);
    if (el) {
      el.addEventListener("input", schedulePersistConfig);
      el.addEventListener("change", schedulePersistConfig);
    }
  });
  ["corOverlayFundo", "opacidadeOverlayFundo", "corLinhas", "opacidadeLinhas", "zoomBackground"].forEach(function (id) {
    var el = byId(id);
    if (el) {
      el.addEventListener("input", function () {
        schedulePersistConfig();
        render();
      });
    }
  });
  var corrImp = byId("correcaoImpressao");
  if (corrImp) {
    corrImp.addEventListener("input", function () {
      schedulePersistConfig();
      render();
    });
    corrImp.addEventListener("change", schedulePersistConfig);
  }
  byId("btnImprimir").addEventListener("click", function () {
    if (container.classList.contains("print-blocked")) {
      return;
    }
    var btn = this;
    if (btn.getAttribute("data-print-png-busy") === "1") {
      return;
    }
    btn.setAttribute("data-print-png-busy", "1");
    btn.disabled = true;
    var prevLabel = btn.textContent;
    btn.textContent = "A preparar PNG…";
    prepareSheetFramesForPrintPng()
      .then(function (backups) {
        btn.textContent = prevLabel;
        attachAfterPrintRestore(backups, function () {
          btn.disabled = false;
          btn.removeAttribute("data-print-png-busy");
        });
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            window.print();
          });
        });
      })
      .catch(function () {
        btn.textContent = prevLabel;
        btn.disabled = false;
        btn.removeAttribute("data-print-png-busy");
        window.print();
      });
  });
  var btnZipEl = byId("btnBaixarZip");
  if (btnZipEl) {
    btnZipEl.addEventListener("click", function () {
      if (container.classList.contains("print-blocked")) {
        return;
      }
      if (btnZipEl.getAttribute("data-zip-busy") === "1") {
        return;
      }
      btnZipEl.setAttribute("data-zip-busy", "1");
      btnZipEl.disabled = true;
      var prevZipLabel = btnZipEl.textContent;
      btnZipEl.textContent = "A gerar ZIP…";
      buildZipFromPreviewPngBlob()
        .then(function (blob) {
          var d = new Date();
          var stamp =
            d.getFullYear() +
            "-" +
            (d.getMonth() + 1 < 10 ? "0" : "") +
            (d.getMonth() + 1) +
            "-" +
            (d.getDate() < 10 ? "0" : "") +
            d.getDate();
          triggerDownloadBlob(blob, "caixinha-a4-" + stamp + ".zip");
        })
        .catch(function () {
          var al = byId("alertas");
          if (al) {
            al.insertAdjacentHTML(
              "afterbegin",
              '<div class="bxg-alert bxg-alert--error">Não foi possível gerar o ZIP. Clique em <strong>Atualizar</strong> e tente de novo.</div>'
            );
          }
        })
        .then(function () {
          btnZipEl.textContent = prevZipLabel;
          btnZipEl.removeAttribute("data-zip-busy");
          var blocked = container.classList.contains("print-blocked");
          btnZipEl.disabled = blocked;
          btnZipEl.setAttribute("aria-disabled", blocked ? "true" : "false");
        });
    });
  }
  function onWindowResize() {
    clearTimeout(resizeDebounceTimer);
    resizeDebounceTimer = setTimeout(render, 120);
  }
  window.addEventListener("resize", onWindowResize);

  function onBeforeUnloadPersist() {
    persistConfig();
  }
  window.addEventListener("beforeunload", onBeforeUnloadPersist);

  loadPersistedConfig();

  (function preloadBackgroundTexture() {
    var img = new Image();
    img.onload = function () {
      invalidateHiResTileCache();
      bgTextureImgEl = img;
      bgTexturePxW = img.naturalWidth;
      bgTexturePxH = img.naturalHeight;
      render();
    };
    img.onerror = function () {
      bgTextureImgEl = null;
      invalidateHiResTileCache();
    };
    img.src = backgroundAssetSrc;
  })();

  render();

  return function unmountMakeABox() {
    clearTimeout(persistTimer);
    clearTimeout(resizeDebounceTimer);
    window.removeEventListener("resize", onWindowResize);
    window.removeEventListener("beforeunload", onBeforeUnloadPersist);
    persistConfig();
    container.innerHTML = "";
    container.classList.remove("bxg-root", "print-blocked");
  };
}
